// # Personal Information Signup Component (Step 2)
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../../auth/firebase';
import AuthHeader from '../../components/AuthHeader';
import './styles/common.css';
import './styles/personal.css';
import { countryCodes, getCountryDataByNationality } from '../../../utils/phone_code';
import { nationalityData } from '../../../utils/nationality_data';
import { formatPhoneNumber } from '../../../utils/phone_format';
import { LogoFade } from './more_components/auth_animation';
import { updateDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import SignupProgress from './more_components/progress_bar';
import LoadingSpinner from '../../components/LoadingSpinner';

function SignupPersonal() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    middleName: '',
    lastName: '',
    gender: '',
    nationality: 'EGYPT',
    phoneNumber: '',
    selectedCountry: { code: '+20', name_en: 'Egypt', flag: '🇪��' }
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredNationalities, setFilteredNationalities] = useState([]);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const usernameDebounceTimer = useRef(null);
  const selectRef = useRef(null);
  const [userModifiedPhoneCode, setUserModifiedPhoneCode] = useState(false);

  // # Check username availability
  const checkUsernameAvailability = async (username) => {
    if (!username) return;
    
    try {
      setIsCheckingUsername(true);
      const date = new Date();
      const docId = date.getFullYear().toString();
      
      const uniqueRef = doc(db, 'unique', docId);
      const uniqueDoc = await getDoc(uniqueRef);
      
      if (!uniqueDoc.exists()) {
        setUsernameError('');
        return true;
      }
      
      const usernames = uniqueDoc.data().usernames || [];
      const isAvailable = !usernames.includes(username.toLowerCase());
      
      setUsernameError(isAvailable ? '' : 'This username is already taken');
      return isAvailable;
    } catch (error) {
      console.error('Error checking username:', error);
      setUsernameError('Error checking username availability');
      return false;
    } finally {
      setIsCheckingUsername(false);
    }
  };

  // # Handle username change with debounce
  const handleUsernameChange = (e) => {
    const sanitizedUsername = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setFormData(prev => ({
      ...prev,
      username: sanitizedUsername
    }));

    // Clear previous timer
    if (usernameDebounceTimer.current) {
      clearTimeout(usernameDebounceTimer.current);
    }

    // Set new timer for username check
    usernameDebounceTimer.current = setTimeout(() => {
      if (sanitizedUsername.length >= 3) {
        checkUsernameAvailability(sanitizedUsername);
      } else if (sanitizedUsername.length > 0) {
        setUsernameError('Username must be at least 3 characters long');
      } else {
        setUsernameError('');
      }
    }, 500);
  };

  // # Clean up debounce timer
  useEffect(() => {
    return () => {
      if (usernameDebounceTimer.current) {
        clearTimeout(usernameDebounceTimer.current);
      }
    };
  }, []);

  // # Filter nationalities based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredNationalities([]);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = Object.entries(nationalityData).filter(([key, data]) => {
      return data.searchKeys.some(searchKey => searchKey.toLowerCase().includes(query)) ||
             data.name.toLowerCase().includes(query);
    });

    setFilteredNationalities(filtered);
  }, [searchQuery]);

  // # Group nationalities by region
  const groupedNationalities = {
    'Arab Countries': Object.entries(nationalityData).filter(([_, data]) => data.searchKeys.includes('arab')),
    'Asia': Object.entries(nationalityData).filter(([_, data]) => data.searchKeys.includes('asia')),
    'Europe': Object.entries(nationalityData).filter(([_, data]) => data.searchKeys.includes('europe')),
    'Americas': Object.entries(nationalityData).filter(([_, data]) => data.searchKeys.includes('americas')),
    'Oceania': Object.entries(nationalityData).filter(([_, data]) => data.searchKeys.includes('oceania'))
  };

  // # Close select when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsSelectOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // # Handle nationality selection with automatic phone code update
  const handleNationalitySelect = (key) => {
    const selectedNationalityData = nationalityData[key];
    
    // # Update nationality with the exact key from nationalityData
    setFormData(prev => {
      const updates = { ...prev, nationality: key };
      
      // # Only auto-update phone code if user hasn't manually changed it
      if (!userModifiedPhoneCode && selectedNationalityData) {
        const countryData = getCountryDataByNationality(selectedNationalityData.name);
        if (countryData) {
          updates.selectedCountry = countryData;
        }
      }
      
      return updates;
    });
    
    setIsSelectOpen(false);
  };

  // # Get selected nationality data with flag
  const selectedNationality = nationalityData[formData.nationality] || {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      // # Final username availability check
      const isUsernameAvailable = await checkUsernameAvailability(formData.username);
      if (!isUsernameAvailable) {
        return;
      }

      // # Add username to unique collection
      const date = new Date();
      const docId = date.getFullYear().toString();
      const uniqueRef = doc(db, 'unique', docId);
      
      await setDoc(uniqueRef, {
        usernames: [...(await getDoc(uniqueRef).then(doc => doc.exists() ? doc.data().usernames : [])), formData.username]
      }, { merge: true });
      
      // # Store data in localStorage instead of sessionStorage
      localStorage.setItem('personalData', JSON.stringify(formData));
      localStorage.setItem('lastSignupStep', '3');
      
      // # Update user document with progress
      const user = auth.currentUser;
      if (user) {
        await updateDoc(doc(db, 'users', user.uid), {
          signupStep: 3,
          username: formData.username,
          'metadata.updatedAt': new Date().toISOString()
        });
      }
      
      // # Navigate to academic info
      navigate('/signup/academic');
    } catch (error) {
      console.error('Error saving personal data:', error);
      setUsernameError('Error saving username. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div>
      <AuthHeader />
      <SignupProgress currentStep={2} />
      <div className="sgnp_psn_signup-container sgnp_psn_personal-step">
        <div className="sgnp_psn_signup-logo-section sgnp_cmn_flex-center">
          <LogoFade src="/icons/signup/logo512.png" />
        </div>
        <div className="sgnp_psn_signup-form-section">
          <h2>Personal Information</h2>
          <form onSubmit={handleSubmit}>
            {/* Personal Information Fields */}
            <div className="sgnp_psn_form-group">
              <div className="sgnp_psn_input-group">
                <label className="sgnp_psn_input-label sgnp_psn_required-field">Username</label>
                <div className="sgnp_psn_username-input-container">
                  <span className="sgnp_psn_username-prefix">@</span>
                  <input
                    type="text"
                    name="username"
                    placeholder="Choose a unique username"
                    value={formData.username}
                    onChange={handleUsernameChange}
                    className={`sgnp_cmn_input ${usernameError ? 'sgnp_psn_error' : ''}`}
                    required
                  />
                </div>
                {isCheckingUsername && <div className="sgnp_psn_checking-message">Checking username availability...</div>}
                {usernameError && <div className="sgnp_psn_error-message">{usernameError}</div>}
              </div>

              <div className="sgnp_psn_input-group">
                <label className="sgnp_psn_input-label sgnp_psn_required-field">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="sgnp_cmn_input"
                  required
                />
              </div>

              <div className="sgnp_psn_input-group">
                <label className="sgnp_psn_input-label">Middle Name</label>
                <input
                  type="text"
                  name="middleName"
                  placeholder="Enter your middle name (optional)"
                  value={formData.middleName}
                  onChange={handleChange}
                  className="sgnp_cmn_input"
                />
              </div>

              <div className="sgnp_psn_input-group">
                <label className="sgnp_psn_input-label sgnp_psn_required-field">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="sgnp_cmn_input"
                  required
                />
              </div>

              <div className="sgnp_psn_input-group">
                <label className="sgnp_psn_input-label sgnp_psn_required-field">Gender</label>
                <select name="gender" value={formData.gender} onChange={handleChange} className="sgnp_cmn_input" required>
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div className="sgnp_psn_input-group">
                <label className="sgnp_psn_input-label sgnp_psn_required-field">Nationality</label>
                <div className="sgnp_psn_nationality-container">
                  <div className="sgnp_psn_custom-select" ref={selectRef}>
                    <div 
                      className="sgnp_psn_select-header"
                      onClick={() => setIsSelectOpen(!isSelectOpen)}
                    >
                      {selectedNationality.flag && (
                        <span className="sgnp_psn_flag">{selectedNationality.flag}</span>
                      )}
                      <span>{selectedNationality.name || 'Select Nationality'}</span>
                    </div>
                    <div className={`sgnp_psn_select-options ${isSelectOpen ? 'open' : ''}`}>
                      {searchQuery.trim() ? (
                        <div className="sgnp_psn_option-group">
                          <div className="sgnp_psn_option-group-label">Search Results</div>
                          {filteredNationalities.map(([key, data]) => (
                            <div
                              key={key}
                              className="sgnp_psn_option"
                              onClick={() => handleNationalitySelect(key)}
                            >
                              <span className="sgnp_psn_flag">{data.flag}</span>
                              <span>{data.name}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        Object.entries(groupedNationalities).map(([region, countries]) => (
                          <div key={region} className="sgnp_psn_option-group">
                            <div className="sgnp_psn_option-group-label">{region}</div>
                            {countries.map(([key, data]) => (
                              <div
                                key={key}
                                className="sgnp_psn_option"
                                onClick={() => handleNationalitySelect(key)}
                              >
                                <span className="sgnp_psn_flag">{data.flag}</span>
                                <span>{data.name}</span>
                              </div>
                            ))}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="sgnp_psn_nationality-search">
                    <input
                      type="text"
                      placeholder="Can not find your country? Look it up here..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="sgnp_cmn_input"
                    />
                  </div>
                </div>
              </div>

              <div className="sgnp_psn_input-group">
                <label className="sgnp_psn_input-label sgnp_psn_required-field">Phone Number (WhatsApp)</label>
                <div className="sgnp_psn_phone-input-wrapper">
                  <select
                    className="sgnp_psn_country-code-select sgnp_cmn_input"
                    value={formData.selectedCountry.code}
                    onChange={(e) => {
                      const country = countryCodes.find(c => c.code === e.target.value);
                      setUserModifiedPhoneCode(true); // # Mark that user has manually selected a code
                      setFormData(prev => ({ ...prev, selectedCountry: country }));
                    }}
                  >
                    {countryCodes.map((country) => (
                      <option key={`${country.code}_${country.name_en}`} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    name="phoneNumber"
                    placeholder="Phone number"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className="sgnp_cmn_input"
                    required
                  />
                </div>
              </div>
            </div>
            <button type="submit" className="sgnp_psn_submit-button">Continue</button>
          </form>
          {loading && <div className="sgnp_cmn_flex-center"><LoadingSpinner /></div>}
        </div>
      </div>
    </div>
  );
}

export default SignupPersonal; 